// src/components/adjustments/AdjustmentsPage.js
//
// Standalone Adjustments page (PRD_StandaloneAdjustments C15/C16/C17/C24).
// A flat, company-wide list — visuals per DESIGN-STANDARDS.md (this page is in
// its "Pages Using This Standard" table); structure modeled on OTApprovalPage
// (flat filtered record list + summary + month picker + userMap name
// resolution). The unpaid set (Pending + In-draft) is ALWAYS shown regardless
// of the month filter and sorts OLDEST first — with carry-forward removed
// (C13), this list is the only control that surfaces a missed entry. Paid
// history is bounded by the month picker, newest first.
//
// C8: the employee picker projects ONLY id/name/department/tenure dates —
// never the raw doc (un-migrated employees still carry compensation on the
// base doc). No balance preview, no negative-balance validation here.

import React, { useState, useEffect, useCallback, useMemo, useId } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import { useToast } from "../../contexts/ToastContext";
import Icon from "../icons/Icon";
import Modal from "../modals/Modal";
import EmployeePicker from "../EmployeePicker";
import { loadActiveLocks } from "../../services/dtrLockDerivation";
import { formatPeso as peso } from "../../utils/currency";
import { formatFilteredCount } from "../../utils/footerStats";
import { matchesEmployeeSearch } from "../../utils/employeeSearch";
import {
  ADJ_STATUS,
  resolveAdjustmentStatus,
  isUnpaidStatus,
} from "../../services/adjustmentDerivation";
import {
  EARNING_CATEGORIES,
  DEDUCTION_CATEGORIES,
  LEGACY_CATEGORIES,
  createAdjustment,
  updateAdjustment,
  voidAdjustment,
  fetchUnpaidAdjustments,
  fetchAdjustmentsForMonth,
  sortAdjustmentRows,
  validateTenure,
  validateAmount,
  paidPeriodWarning,
  pageDateClamp,
  partitionBulkSelection,
  eligibleForDate,
  PICKER_NO_DATE,
  bulkPreviewLines,
  createAdjustmentsBulk,
  createAdjustmentsPerRow,
  fetchBatchMembers,
  partitionBatchMembers,
  batchDialogLine,
  voidAdjustmentBatch,
} from "../../services/adjustmentService";
import { toYMDString, todayManilaYMD } from "../../utils/periodDate";
import { canGenerateThirteenthMonth } from "../../services/thirteenthMonthService";
import GenerateThirteenthMonthModal from "./GenerateThirteenthMonthModal";
import { DEFAULT_CUTOFF_START_DAYS } from "../../utils/payCutoffs";
import PwPopover from "../PwPopover";

// Month picker options — current + 5 back (the OTApprovalPage pattern).
const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    options.push({ value, label });
  }
  return options;
};
const MONTH_OPTIONS = getMonthOptions();

const monthBounds = (monthValue) => {
  const [y, m] = monthValue.split("-").map(Number);
  return {
    start: `${monthValue}-01`,
    end: toYMDString(new Date(y, m, 0)), // last day of the month, local
  };
};

const STATUS_BADGE = {
  [ADJ_STATUS.PENDING]: { label: "Pending", bg: "var(--ui-warning-tint)", color: "var(--ui-warning-text)" },
  [ADJ_STATUS.IN_DRAFT]: { label: "In draft run", bg: "var(--ui-primary-tint)", color: "var(--ui-primary-hover)" },
  [ADJ_STATUS.PAID]: { label: "Paid", bg: "var(--ui-success-tint)", color: "var(--ui-success-text)" },
  [ADJ_STATUS.LINK_BROKEN]: { label: "Link broken", bg: "var(--ui-danger-tint)", color: "var(--ui-danger-text)" },
};


const inputStyle = {
  padding: "0.5rem 0.75rem",
  border: "1px solid var(--ui-border)",
  borderRadius: "6px",
  fontSize: "0.8125rem",
  fontFamily: "inherit",
};

// Sort icon — the EmployeeList.js pattern, local to this page (C9).
const SortIcon = ({ field, sortField, sortDirection }) => {
  const isActive = sortField === field;
  const base = { marginLeft: "4px", verticalAlign: "middle" };
  if (isActive && sortDirection === "asc") return <Icon name="arrow-up" size={12} style={base} />;
  if (isActive && sortDirection === "desc") return <Icon name="arrow-down" size={12} style={base} />;
  return <Icon name="chevrons-up-down" size={12} style={{ ...base, opacity: 0.5 }} />;
};

// A sortable header. The pointer + hover wash (DESIGN-STANDARDS.md §7) live
// HERE, per column — never on the shared `th` object, or Added By / Actions
// would read as sortable too (C9). The wash is inline state because this
// page styles inline and has no stylesheet :hover to lean on.
const SortableTh = ({ field, label, style, sortField, sortDirection, onSort }) => {
  const [hover, setHover] = useState(false);
  return (
    <th
      onClick={() => onSort(field)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, cursor: "pointer", background: hover ? "rgba(255,255,255,0.1)" : undefined }}
    >
      {label}
      <SortIcon field={field} sortField={sortField} sortDirection={sortDirection} />
    </th>
  );
};

const AdjustmentsPage = () => {
  const { user, company, hasPermission } = useAuth();
  const canEdit = hasPermission("adj:edit");
  const toast = useToast();

  // Data state
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]); // merged unpaid ∪ month, status resolved
  const [employees, setEmployees] = useState([]); // C8-projected fields only
  const [userMap, setUserMap] = useState({});
  const [runs, setRuns] = useState([]);
  const [cutoffStartDays, setCutoffStartDays] = useState(DEFAULT_CUTOFF_START_DAYS);

  // Filter state
  const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[0].value);
  const [statusFilter, setStatusFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  // PRD_AdjustmentsEligibilityAndSort C8: "" = no active sort → C15's default
  // order. The empty string, not null — the helper's guard is falsy.
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  // Voided rows live behind the status filter (test-impl S1: one control less
  // in the bar; "show me voided ones" is a status question).
  const VOIDED_FILTER = "voided";

  // Modal state
  const [modal, setModal] = useState(null); // { mode: 'create' } | { mode: 'edit', row }
  const [voidConfirm, setVoidConfirm] = useState(null); // row
  const [bulkOpen, setBulkOpen] = useState(false); // PRD_BulkAdjustments C7
  const [thirteenthOpen, setThirteenthOpen] = useState(false); // PRD_ThirteenthMonthPay C1
  const [batchMembers, setBatchMembers] = useState(null); // live members of voidConfirm's batch (C8 preview)
  const [saving, setSaving] = useState(false);
  const voidTitleId = useId();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const userDocId = user?.userData?.userDocId || user?.uid;

  // ── Data load ─────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const { start, end } = monthBounds(selectedMonth);
      const [indexRuns, empSnap, userSnap, payrollSettings] = await Promise.all([
        loadActiveLocks(company.id), // throws on read error → fail-closed catch below
        getDocs(query(collection(db, "companies", company.id, "employees"), where("role", "==", "employee"))),
        getDocs(collection(db, "companies", company.id, "users")),
        getDoc(doc(db, "companies", company.id, "settings", "payroll")),
      ]);
      const [unpaid, monthRows] = await Promise.all([
        fetchUnpaidAdjustments(company.id, indexRuns),
        fetchAdjustmentsForMonth(company.id, start, end),
      ]);

      // C8: project ONLY what the picker/table needs — never the raw doc.
      setEmployees(
        empSnap.docs
          .map((d) => {
            const data = d.data();
            return {
              employeeId: data.employeeId,
              fullName:
                data.personalInfo?.fullName ||
                `${data.personalInfo?.firstName || ""} ${data.personalInfo?.lastName || ""}`.trim(),
              department: data.employment?.department || "",
              startDate: toYMDString(data.employment?.startDate),
              endDate: toYMDString(data.employment?.endDate),
            };
          })
          .filter((e) => e.employeeId),
      );

      const uMap = {};
      userSnap.docs.forEach((d) => {
        const data = d.data();
        uMap[d.id] = data.fullName || data.displayName || data.email || "Unknown User";
      });
      setUserMap(uMap);

      const storedCutoffs = payrollSettings.exists() ? payrollSettings.data().cutoffStartDays : undefined;
      if (Array.isArray(storedCutoffs) && storedCutoffs.length === 2) {
        setCutoffStartDays(storedCutoffs);
      }

      // Merge (dedupe by id) + resolve status once per row.
      const byId = new Map();
      [...unpaid, ...monthRows].forEach((r) => byId.set(r.id, r));
      const merged = [...byId.values()].map((r) => ({
        ...r,
        resolved: resolveAdjustmentStatus(r.consumedRunId, indexRuns),
      }));

      setRuns(indexRuns);
      setRows(merged);
    } catch (err) {
      console.error("[AdjustmentsPage] load failed:", err);
      // Fail-closed: without the index, consumed entries can't be resolved —
      // show nothing actionable rather than guess (same posture as OT Approval).
      toast.error("Couldn't load adjustments (incl. payroll run state) — please retry.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [company?.id, selectedMonth, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Derived view ──────────────────────────────────────────────────────────
  const employeeById = useMemo(() => {
    const m = {};
    employees.forEach((e) => {
      m[e.employeeId] = e;
    });
    return m;
  }, [employees]);

  const departments = useMemo(
    () => [...new Set(employees.map((e) => e.department).filter(Boolean))].sort(),
    [employees],
  );

  const { start: monthStart, end: monthEnd } = monthBounds(selectedMonth);

  const visibleRows = useMemo(() => {
    const voidedOnly = statusFilter === VOIDED_FILTER;
    const filtered = rows.filter((r) => {
      // "Voided" is a status-filter mode: show ONLY voided rows there,
      // exclude them everywhere else.
      if (voidedOnly ? !r.metadata?.isDeleted : r.metadata?.isDeleted) return false;
      const unpaidRow = isUnpaidStatus(r.resolved.status);
      // Month filter bounds PAID/broken history only; unpaid always shows (C16).
      if (!unpaidRow && !(r.effectiveDate >= monthStart && r.effectiveDate <= monthEnd)) return false;
      if (statusFilter && !voidedOnly && r.resolved.status !== statusFilter) return false;
      const emp = employeeById[r.employeeId];
      if (departmentFilter && (emp?.department || "") !== departmentFilter) return false;
      // emp is undefined when the employee is no longer on the roster, so the
      // row's own ID rides as an extra term with description and category (C3).
      if (!matchesEmployeeSearch(emp, searchTerm, [r.employeeId, r.description, r.category])) return false;
      return true;
    });
    // Unpaid oldest-first (the forgotten entry surfaces first), then paid
    // newest-first (C15); a header sort applies WITHIN each block (C7).
    return sortAdjustmentRows(filtered, sortField, sortDirection, employeeById);
  }, [rows, monthStart, monthEnd, statusFilter, departmentFilter, searchTerm, employeeById, sortField, sortDirection]);

  // House sort toggle (EmployeeList.js): new column → asc, same column → flip.
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Filter-aware footer stats (PRD_FilterAwareFooterStats C1/C3): the buckets
  // count the FILTERED rows (deleted-exclusion kept, so Voided mode reads
  // "0 of N" — consistent with today's Net); the page-wide pending count stays
  // as the dual "of N" base. The month picker is a scope selector, not a
  // filter (visibleRows month-bounds paid rows either way).
  const filterActive =
    Boolean(searchTerm) || Boolean(statusFilter) || Boolean(departmentFilter);
  const stats = useMemo(() => {
    const pagePending = rows.filter(
      (r) => !r.metadata?.isDeleted && r.resolved.status === ADJ_STATUS.PENDING,
    ).length;
    const shown = visibleRows.filter((r) => !r.metadata?.isDeleted);
    const pending = shown.filter((r) => r.resolved.status === ADJ_STATUS.PENDING).length;
    const inDraft = shown.filter((r) => r.resolved.status === ADJ_STATUS.IN_DRAFT).length;
    const paidMonth = shown.filter((r) => r.resolved.status === ADJ_STATUS.PAID).length;
    const net = shown.reduce(
      (sum, r) => sum + (r.type === "earning" ? r.amount : -r.amount),
      0,
    );
    return {
      pendingDisplay: formatFilteredCount(pending, pagePending, filterActive),
      inDraft,
      paidMonth,
      net,
    };
  }, [rows, visibleRows, filterActive]);

  // C24 — page date clamp: previous cycle start … current cycle end, from the
  // company's pay calendar (never calendar months — a cycle can cross one).
  const dateClamp = useMemo(
    () => pageDateClamp(cutoffStartDays, toYMDString(new Date())),
    [cutoffStartDays],
  );

  const handleVoid = async () => {
    const row = voidConfirm;
    setVoidConfirm(null);
    try {
      await voidAdjustment(company.id, row.id, userDocId);
      toast.success("Adjustment deleted.");
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // C8 preview: when the row belongs to a batch, fetch its live members once
  // per dialog open; the counts are derived at render from the page's runs
  // (so a runs refresh never resets the dialog). Until the fetch resolves —
  // and if it fails — the dialog is the plain single-delete (fail closed to
  // today's behaviour). The confirm recomputes from fresh reads inside
  // voidAdjustmentBatch.
  useEffect(() => {
    setBatchMembers(null);
    if (!voidConfirm?.batchId || !company?.id) return undefined;
    let cancelled = false;
    fetchBatchMembers(company.id, voidConfirm.batchId)
      .then((members) => {
        if (!cancelled) setBatchMembers(members);
      })
      .catch((err) => console.error("[AdjustmentsPage] batch preview failed:", err));
    return () => {
      cancelled = true;
    };
  }, [voidConfirm, company?.id]);

  const handleVoidBatch = async () => {
    const row = voidConfirm;
    setVoidConfirm(null);
    try {
      const { voided, frozen, failed, error } = await voidAdjustmentBatch(company.id, row.batchId, userDocId);
      const left = frozen ? ` ${frozen} already paid — left as is.` : "";
      if (failed > 0) {
        const why = error?.message ? ` (${error.message})` : "";
        toast.error(`Deleted ${voided} of ${voided + failed}. ${failed} could not be deleted${why} — open Delete again to retry.`);
      } else {
        toast.success(`Deleted ${voided} ${voided === 1 ? "entry" : "entries"}.${left}`);
      }
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };
  // F1: null unless the batch has more than one live deletable member — then
  // the dialog gains one sentence and one button; otherwise it is unchanged.
  const batchLive = batchMembers ? partitionBatchMembers(batchMembers, runs).live.length : 0;
  const batchLine = batchMembers ? batchDialogLine(batchMembers.length, batchLive) : null;

  // ── Render ────────────────────────────────────────────────────────────────
  const th = {
    padding: "14px 16px",
    textAlign: "left",
    fontWeight: "600",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "white",
    userSelect: "none",
    whiteSpace: "nowrap",
  };
  const td = { padding: "12px 16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" };

  const statusBadge = (status) => {
    const s = STATUS_BADGE[status];
    return (
      <span
        style={{
          background: s.bg,
          color: s.color,
          padding: "0.25rem 0.75rem",
          borderRadius: "20px",
          fontSize: "0.75rem",
          fontWeight: "600",
          whiteSpace: "nowrap",
        }}
      >
        {s.label}
      </span>
    );
  };

  const tenureHint = (row) => {
    const emp = employeeById[row.employeeId];
    if (!emp || row.resolved.status !== ADJ_STATUS.PENDING) return null;
    if (!validateTenure(row.effectiveDate, emp)) return null;
    return (
      <span style={{ marginLeft: "0.5rem", display: "inline-block" }}>
        <PwPopover
          ariaLabel="Why payroll will skip this entry"
          strategy="fixed"
          trigger={
            <span
              style={{
                display: "inline-block",
                background: "var(--ui-warning-tint)",
                color: "var(--ui-warning-text)",
                padding: "0.125rem 0.5rem",
                borderRadius: "10px",
                fontSize: "0.6875rem",
                fontWeight: "600",
                whiteSpace: "nowrap",
              }}
            >
              outside employment window
            </span>
          }
        >
          This date is outside the employee&apos;s employment window, so payroll
          will skip it. Re-date or delete the entry.
        </PwPopover>
      </span>
    );
  };

  const renderRow = (row) => {
    const emp = employeeById[row.employeeId];
    const voided = row.metadata?.isDeleted;
    return (
      <tr
        key={row.id}
        style={{
          borderBottom: "1px solid var(--ui-surface-recessed)",
          transition: "background-color 0.2s",
          opacity: voided ? 0.55 : 1,
        }}
      >
        <td style={td}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, var(--ui-primary-tint), var(--ui-primary-tint))",
                color: "var(--ui-primary)",
                fontSize: "13px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {(emp?.fullName || "?")
                .split(" ")
                .map((w) => w[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </div>
            <div>
              <div style={{ whiteSpace: "nowrap" }}>{emp?.fullName || `Employee ${row.employeeId}`}</div>
              <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontVariantNumeric: "tabular-nums" }}>
                EMP-{row.employeeId}
              </div>
            </div>
          </div>
        </td>
        {/* Category with the description stacked underneath (test-impl S2:
            no Type/Description columns — the signed, colored amount carries
            the type, and 9 columns forced a horizontal scroll). */}
        <td style={td}>
          <div style={{ whiteSpace: "nowrap" }}>
            {[...EARNING_CATEGORIES, ...DEDUCTION_CATEGORIES, ...LEGACY_CATEGORIES].find((c) => c.value === row.category)?.label ||
              row.category}
          </div>
          {row.description && (
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--ui-text-muted)",
                fontWeight: 400,
                maxWidth: "220px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.description}
            </div>
          )}
        </td>
        <td
          style={{
            ...td,
            fontVariantNumeric: "tabular-nums",
            textAlign: "right",
            whiteSpace: "nowrap",
            color: row.type === "earning" ? "var(--ui-success-text)" : "var(--ui-danger-text)",
            fontWeight: 600,
          }}
        >
          {row.type === "earning" ? "+" : "−"}
          {peso(row.amount)}
        </td>
        <td style={{ ...td, fontVariantNumeric: "tabular-nums" }}>
          {row.effectiveDate}
          {tenureHint(row)}
        </td>
        <td style={td}>
          {voided ? (
            <span
              style={{
                background: "var(--ui-surface-recessed)",
                color: "var(--ui-text-muted)",
                padding: "0.25rem 0.75rem",
                borderRadius: "20px",
                fontSize: "0.75rem",
                fontWeight: "600",
              }}
            >
              Deleted
            </span>
          ) : (
            statusBadge(row.resolved.status)
          )}
        </td>
        <td style={{ ...td, color: "var(--ui-text-muted)", fontWeight: 400 }}>{userMap[row.createdBy] || "—"}</td>
        <td style={{ ...td, whiteSpace: "nowrap" }}>
          {canEdit && !voided && row.resolved.editable && (
            <>
              <button
                onClick={() => setModal({ mode: "edit", row })}
                style={{
                  padding: "0.375rem 0.625rem",
                  border: "1px solid var(--ui-border)",
                  borderRadius: "6px",
                  background: "white",
                  cursor: "pointer",
                  marginRight: "0.5rem",
                  fontSize: "0.8125rem",
                }}
              >
                <Icon name="pencil" size={14} />
              </button>
              <button
                onClick={() => setVoidConfirm(row)}
                style={{
                  padding: "0.375rem 0.625rem",
                  border: "1px solid var(--ui-danger)",
                  color: "var(--ui-danger-text)",
                  borderRadius: "6px",
                  background: "white",
                  cursor: "pointer",
                  fontSize: "0.8125rem",
                }}
              >
                <Icon name="trash-2" size={14} />
              </button>
            </>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div style={{ fontFamily: "var(--font-family-primary)" }}>
      <div style={{ width: "100%" }}>
        {/* PRD_ThirteenthMonthPay C1/C7 — Generate 13th Month sits on the title row:
            a third button in the controls bar overflowed it at 1440/1366/1280px
            (measured in Chromium at /impl-review). C7: also needs payroll:view. */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.75rem",
            margin: isMobile ? "0 0 1rem 0" : "0 0 0.75rem 0",
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? "1.125rem" : "1.25rem",
              fontWeight: "700",
              color: "var(--ui-primary)",
              margin: 0,
            }}
          >
            Adjustments
          </h1>
          {canEdit && canGenerateThirteenthMonth(hasPermission) && (
            <button
              onClick={() => setThirteenthOpen(true)}
              style={{
                padding: "0.5rem 1rem",
                background: "white",
                color: "var(--ui-primary)",
                border: "1px solid var(--ui-primary)",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "0.8125rem",
                cursor: "pointer",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.375rem",
              }}
            >
              <Icon name="calendar" size={14} /> Generate 13th Month
            </button>
          )}
        </div>

        {/* Mobile stats cards (desktop shows them in the footer) */}
        {isMobile && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
            {[
              { label: "Pending", value: stats.pendingDisplay, color: "var(--ui-warning)" },
              { label: "In draft run", value: stats.inDraft, color: "var(--ui-primary)" },
              { label: "Paid (month)", value: stats.paidMonth, color: "var(--ui-success)" },
              { label: "Net impact", value: peso(stats.net), color: "var(--ui-primary)" },
            ].map((c) => (
              <div
                key={c.label}
                style={{
                  position: "relative",
                  background: "white",
                  borderRadius: "8px",
                  border: "1px solid var(--ui-border)",
                  padding: "0.875rem 1rem",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: "15%",
                    bottom: "15%",
                    width: "4px",
                    background: c.color,
                    borderRadius: "0 2px 2px 0",
                  }}
                />
                <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>
                  {c.label}
                </div>
                <div style={{ fontSize: "18px", fontWeight: 600, fontVariantNumeric: "tabular-nums", color: "var(--ui-text)" }}>
                  {c.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Controls bar */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            gap: "0.75rem",
            padding: isMobile ? "1rem" : "0.625rem 1rem",
            background: "white",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            marginBottom: "0.75rem",
          }}
        >
          {/* Search first + flex:1 (the OTApprovalPage bar pattern) — it absorbs
              the slack so the CTA never overflows the row (test-impl S1). */}
          <input
            type="text"
            placeholder="Search employee, category, description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ ...inputStyle, flex: 1, minWidth: "140px" }}
          />
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} style={{ ...inputStyle, minWidth: "130px" }}>
            {MONTH_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ ...inputStyle, minWidth: "120px" }}>
            <option value="">All statuses</option>
            <option value={ADJ_STATUS.PENDING}>Pending</option>
            <option value={ADJ_STATUS.IN_DRAFT}>In draft run</option>
            <option value={ADJ_STATUS.PAID}>Paid</option>
            <option value={ADJ_STATUS.LINK_BROKEN}>Link broken</option>
            <option value={VOIDED_FILTER}>Deleted</option>
          </select>
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            style={{ ...inputStyle, minWidth: "140px" }}
          >
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          {canEdit && (
            <>
              {!isMobile && <div style={{ width: "1px", height: "24px", background: "var(--ui-border)", flexShrink: 0 }} />}
              <button
                onClick={() => setModal({ mode: "create" })}
                style={{
                  padding: "0.5rem 1rem",
                  background: "var(--ui-primary)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "600",
                  fontSize: "0.8125rem",
                  cursor: "pointer",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.375rem",
                }}
              >
                <Icon name="plus" size={14} /> Add Adjustment
              </button>
              <button
                onClick={() => setBulkOpen(true)}
                style={{
                  padding: "0.5rem 1rem",
                  background: "white",
                  color: "var(--ui-primary)",
                  border: "1px solid var(--ui-primary)",
                  borderRadius: "8px",
                  fontWeight: "600",
                  fontSize: "0.8125rem",
                  cursor: "pointer",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.375rem",
                }}
              >
                <Icon name="users" size={14} /> Bulk Add
              </button>
            </>
          )}
        </div>

        {/* Table */}
        <div style={{ background: "white", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", overflowX: "auto", overflowY: "hidden" }}>
          {loading ? (
            <div style={{ padding: "3rem", textAlign: "center", color: "var(--ui-text-muted)" }}>Loading adjustments…</div>
          ) : visibleRows.length === 0 ? (
            <div style={{ padding: "3rem", textAlign: "center", color: "var(--ui-text-muted)" }}>
              No adjustments{statusFilter || searchTerm || departmentFilter ? " match your filters" : " yet"}.
              {canEdit && !statusFilter && !searchTerm && " Use “Add Adjustment” to record one."}
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--ui-gradient-primary)" }}>
                  <SortableTh field="employee" label="Employee" style={th} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableTh field="category" label="Category" style={th} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableTh field="amount" label="Amount" style={{ ...th, textAlign: "right" }} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableTh field="effectiveDate" label="Effective Date" style={th} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableTh field="status" label="Status" style={th} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <th style={th}>Added By</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>{visibleRows.map(renderRow)}</tbody>
            </table>
          )}

          {/* Footer stats (desktop) */}
          {!isMobile && !loading && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 20px",
                background: "linear-gradient(135deg, var(--ui-bg) 0%, var(--ui-surface-recessed) 100%)",
                borderTop: "1px solid var(--ui-border)",
              }}
            >
              <div style={{ display: "flex", gap: "28px" }}>
                {[
                  { label: "Pending", value: stats.pendingDisplay, primary: true },
                  { label: "In Draft Run", value: stats.inDraft },
                  { label: "Paid This Month", value: stats.paidMonth },
                  { label: "Net Impact (Shown)", value: peso(stats.net) },
                ].map((s) => (
                  <div key={s.label}>
                    <div
                      style={{
                        fontSize: s.primary ? "20px" : "16px",
                        fontWeight: "600",
                        fontVariantNumeric: "tabular-nums",
                        color: s.primary ? "var(--ui-primary)" : "var(--ui-text)",
                      }}
                    >
                      {s.value}
                    </div>
                    <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: "13px", fontStyle: "italic", color: "var(--ui-text-muted)" }}>
                Unpaid entries always show first (oldest on top), whatever the month filter
              </div>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <AdjustmentModal
          mode={modal.mode}
          row={modal.row}
          employees={employees}
          runs={runs}
          dateClamp={dateClamp}
          saving={saving}
          onClose={() => setModal(null)}
          onSave={async (form) => {
            setSaving(true);
            try {
              const emp = employees.find((e) => e.employeeId === form.employeeId);
              if (modal.mode === "create") {
                await createAdjustment(company.id, form, emp || {}, userDocId);
                toast.success("Adjustment recorded.");
              } else {
                await updateAdjustment(company.id, modal.row.id, form, emp || {}, userDocId);
                toast.success("Adjustment updated.");
              }
              setModal(null);
              fetchData();
            } catch (err) {
              toast.error(err.message);
            } finally {
              setSaving(false);
            }
          }}
        />
      )}

      {thirteenthOpen && (
        <GenerateThirteenthMonthModal
          companyId={company.id}
          employees={employees}
          cutoffStartDays={cutoffStartDays}
          dateClamp={dateClamp}
          todayYMD={todayManilaYMD()}
          saving={saving}
          onClose={() => setThirteenthOpen(false)}
          onSave={async (rowsToCreate, data) => {
            setSaving(true);
            try {
              const { created, failed, error } = await createAdjustmentsPerRow(
                company.id, rowsToCreate, data, userDocId,
              );
              if (failed > 0) {
                const why = error?.message ? ` (${error.message})` : "";
                toast.error(
                  created === 0
                    ? `Nothing was saved — ${failed} ${failed === 1 ? "entry" : "entries"} could not be created${why}.`
                    : `Created ${created} of ${created + failed}. ${failed} could not be saved${why} — open Delete on any of them to remove the whole batch.`,
                );
              } else {
                toast.success(`Created ${created} ${created === 1 ? "entry" : "entries"}.`);
              }
              // As Bulk Add (C6): close and refetch on any outcome — the undo lives on a row.
              setThirteenthOpen(false);
              fetchData();
            } catch (err) {
              toast.error(err.message); // validation — nothing was written
            } finally {
              setSaving(false);
            }
          }}
        />
      )}

      {bulkOpen && (
        <BulkAdjustmentModal
          employees={employees}
          runs={runs}
          dateClamp={dateClamp}
          saving={saving}
          onClose={() => setBulkOpen(false)}
          onSave={async ({ partition, form }) => {
            setSaving(true);
            try {
              const { created, failed, error } = await createAdjustmentsBulk(
                company.id, partition.toCreate, form, userDocId,
              );
              const skips = partition.skipped.length ? ` ${partition.skipped.length} skipped.` : "";
              const paid = partition.alreadyPaid
                ? ` ${partition.alreadyPaid} already paid — those will stay Pending.`
                : "";
              if (failed > 0) {
                const why = error?.message ? ` (${error.message})` : "";
                toast.error(
                  created === 0
                    ? `Nothing was saved — ${failed} ${failed === 1 ? "entry" : "entries"} could not be created${why}.`
                    : `Created ${created} of ${created + failed}. ${failed} could not be saved${why} — open Delete on any of them to remove the whole batch.`,
                );
              } else {
                toast.success(`Created ${created} ${created === 1 ? "entry" : "entries"}.${skips}${paid}`);
              }
              // C6: on ANY outcome the modal closes and the page refetches —
              // the committed rows must be on screen, since the undo lives on a row.
              setBulkOpen(false);
              fetchData();
            } catch (err) {
              toast.error(err.message); // validation — nothing was written
            } finally {
              setSaving(false);
            }
          }}
        />
      )}

      {voidConfirm && (
        <Modal
          isOpen
          onClose={() => setVoidConfirm(null)}
          labelledBy={voidTitleId}
          style={{ background: "white", borderRadius: "12px", padding: "1.5rem", maxWidth: "420px", width: "100%" }}
        >
            <h3 id={voidTitleId} style={{ margin: "0 0 0.75rem 0", fontSize: "1rem", color: "var(--ui-text)" }}>Delete this adjustment?</h3>
            <p style={{ fontSize: "0.875rem", color: "var(--ui-text-secondary)", margin: batchLine ? "0 0 0.75rem 0" : "0 0 1.25rem 0" }}>
              {`Deleting removes the ${voidConfirm.description || "entry"} (${peso(voidConfirm.amount)}) from payroll processing. It stays in the audit trail under the “Deleted” status filter and cannot be undone.`}
            </p>
            {batchLine && (
              <p style={{ fontSize: "0.875rem", color: "var(--ui-text-secondary)", margin: "0 0 1.25rem 0" }}>{batchLine}</p>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", flexWrap: batchLine ? "wrap" : undefined }}>
              <button onClick={() => setVoidConfirm(null)} style={{ ...inputStyle, cursor: "pointer", background: "white" }}>
                Cancel
              </button>
              <button
                onClick={handleVoid}
                style={{
                  padding: "0.5rem 1rem",
                  background: batchLine ? "white" : "var(--ui-danger)",
                  color: batchLine ? "var(--ui-danger-text)" : "white",
                  border: batchLine ? "1px solid var(--ui-danger)" : "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  cursor: "pointer",
                }}
              >
                {batchLine ? "Delete just this one" : "Delete Adjustment"}
              </button>
              {batchLine && (
                <button
                  onClick={handleVoidBatch}
                  style={{
                    padding: "0.5rem 1rem",
                    background: "var(--ui-danger)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "0.8125rem",
                    cursor: "pointer",
                  }}
                >
                  Delete all {batchLive}
                </button>
              )}
            </div>
        </Modal>
      )}
    </div>
  );
};

// ── Create / edit modal ─────────────────────────────────────────────────────
// C14: on edit of a consumed entry, employee/type/date are frozen (void +
// recreate to change identity). C24: the date input is clamped to the pay
// calendar (previous cycle start … current cycle end).
const AdjustmentModal = ({ mode, row, employees, runs, dateClamp, saving, onClose, onSave }) => {
  const isEdit = mode === "edit";
  const frozen = isEdit && row.consumedRunId != null;

  const [employeeId, setEmployeeId] = useState(isEdit ? row.employeeId : "");
  const [type, setType] = useState(isEdit ? row.type : "earning");
  const [category, setCategory] = useState(isEdit ? row.category : "");
  const [description, setDescription] = useState(isEdit ? row.description : "");
  const [amount, setAmount] = useState(isEdit ? String(row.amount) : "");
  const [effectiveDate, setEffectiveDate] = useState(isEdit ? row.effectiveDate : "");
  const [error, setError] = useState(null);
  const titleId = useId();

  const categories = type === "earning" ? EARNING_CATEGORIES : DEDUCTION_CATEGORIES;
  const emp = employees.find((e) => e.employeeId === employeeId);

  // Advisory notices (C12c + the landing-run hint), from the index the page already read.
  const warning = employeeId && effectiveDate ? paidPeriodWarning(employeeId, effectiveDate, runs) : null;
  const landingRun = useMemo(() => {
    if (!employeeId || !effectiveDate || warning) return null;
    const draft = (runs || []).find(
      (r) =>
        r?.status === "draft" &&
        r.periodStart &&
        r.periodEnd &&
        r.periodStart <= effectiveDate &&
        effectiveDate <= r.periodEnd &&
        (r.employeeIds || []).map(String).includes(String(employeeId)),
    );
    return draft ? `Will be picked up by the draft run for ${draft.periodStart} – ${draft.periodEnd}.` : null;
  }, [employeeId, effectiveDate, warning, runs]);

  const submit = () => {
    setError(null);
    if (!employeeId) return setError("Select an employee.");
    if (!category) return setError("Select a category.");
    const num = parseFloat(amount);
    const amountError = validateAmount(Number.isNaN(num) ? NaN : num);
    if (amountError) return setError(amountError);
    const tenureError = validateTenure(effectiveDate, emp || {});
    if (tenureError) return setError(tenureError);
    onSave({ employeeId, type, category, description, amount: num, effectiveDate });
  };

  const field = { display: "block", width: "100%", marginBottom: "0.75rem", ...inputStyle };
  const label = { fontSize: "0.75rem", fontWeight: 600, color: "var(--ui-text-secondary)", marginBottom: "0.25rem", display: "block" };

  return (
    <Modal
      isOpen
      onClose={onClose}
      busy={saving}
      labelledBy={titleId}
      style={{ background: "white", borderRadius: "12px", maxWidth: "480px", width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      {/* The body scrolls, not the dialog, so the shell's × stays pinned. */}
      <div style={{ padding: "1.5rem", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 id={titleId} style={{ margin: 0, fontSize: "1rem", color: "var(--ui-text)" }}>
            {isEdit ? "Edit Adjustment" : "Add Adjustment"}
          </h3>
        </div>

        {frozen && (
          <div style={{ background: "var(--ui-primary-tint)", color: "var(--ui-primary-hover)", borderRadius: "8px", padding: "0.625rem 0.875rem", fontSize: "0.8125rem", marginBottom: "0.75rem" }}>
            This entry is in a draft payroll run — employee, type and date are locked. Amount and description
            changes are picked up when the run is resumed.
          </div>
        )}

        <label style={label}>Employee</label>
        <EmployeePicker
          employees={employees}
          value={emp ?? null}
          onChange={(row) => setEmployeeId(row ? row.employeeId : "")}
          locked={frozen}
        />

        <label style={label}>Type</label>
        <div style={{ display: "flex", gap: "1rem", marginBottom: "0.75rem" }}>
          {["earning", "deduction"].map((t) => (
            <label key={t} style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.875rem" }}>
              <input
                type="radio"
                checked={type === t}
                disabled={frozen}
                onChange={() => {
                  setType(t);
                  setCategory("");
                }}
              />
              {t === "earning" ? "Earning (add to pay)" : "Deduction (subtract from pay)"}
            </label>
          ))}
        </div>

        <label style={label}>Category</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)} style={field}>
          <option value="">Select category…</option>
          {categories.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>

        <label style={label}>Amount (₱)</label>
        <input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} style={field} />

        <label style={label}>Effective Date</label>
        <input
          type="date"
          value={effectiveDate}
          min={dateClamp.min}
          max={dateClamp.max}
          disabled={frozen}
          onChange={(e) => setEffectiveDate(e.target.value)}
          style={field}
        />

        <label style={label}>Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="e.g. Cash advance released 5 Aug"
          style={{ ...field, resize: "vertical", fontFamily: "inherit" }}
        />

        {warning && (
          <div style={{ background: "var(--ui-warning-tint)", color: "var(--ui-warning-text)", borderRadius: "8px", padding: "0.625rem 0.875rem", fontSize: "0.8125rem", marginBottom: "0.75rem" }}>
            {warning}
          </div>
        )}
        {landingRun && (
          <div style={{ background: "var(--ui-primary-tint)", color: "var(--ui-primary)", borderRadius: "8px", padding: "0.625rem 0.875rem", fontSize: "0.8125rem", marginBottom: "0.75rem" }}>
            {landingRun}
          </div>
        )}
        {error && (
          <div style={{ background: "var(--ui-danger-tint)", color: "var(--ui-danger-text)", borderRadius: "8px", padding: "0.625rem 0.875rem", fontSize: "0.8125rem", marginBottom: "0.75rem" }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "0.5rem" }}>
          <button onClick={onClose} disabled={saving} style={{ ...inputStyle, cursor: "pointer", background: "white" }}>
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            style={{
              padding: "0.5rem 1rem",
              background: "var(--ui-primary)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "0.8125rem",
              cursor: saving ? "wait" : "pointer",
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Adjustment"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ── Bulk modal (PRD_BulkAdjustments C1/C4/C5/C11/F1) ────────────────────────
// One set of values for the whole selection (C1). The partition runs ONCE and
// feeds both the preview block and the submit (C4/C5); the date input takes
// the page's clamp exactly as AdjustmentModal does (parent C24). In-file
// beside AdjustmentModal by design (C7) — the wizard's ManualAdjustments is a
// separate component and is not touched.
const BulkAdjustmentModal = ({ employees, runs, dateClamp, saving, onClose, onSave }) => {
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const titleId = useId();
  const [type, setType] = useState("earning");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [error, setError] = useState(null);

  const categories = type === "earning" ? EARNING_CATEGORIES : DEDUCTION_CATEGORIES;

  // C11: searchable checkbox list, grouped by department with a per-department
  // select-all. No new reads — the page's projected employees carry everything.
  // PRD_AdjustmentsEligibilityAndSort C1/C2: no date → no list (tenure is
  // undefined without one); with a date, only employees employed on it, and
  // the tenure filter runs BEFORE grouping so an emptied department never
  // renders a header whose select-all is vacuously ticked.
  const groups = useMemo(() => {
    if (!effectiveDate) return [];
    const q = search.trim().toLowerCase();
    const byDept = new Map();
    eligibleForDate(
      employees.filter((e) => !q || `${e.fullName} ${e.employeeId}`.toLowerCase().includes(q)),
      effectiveDate,
    ).forEach((e) => {
      const key = e.department || "No department";
      if (!byDept.has(key)) byDept.set(key, []);
      byDept.get(key).push(e);
    });
    return [...byDept.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [employees, search, effectiveDate]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleGroup = (members, allOn) =>
    setSelected((prev) => {
      const next = new Set(prev);
      members.forEach((e) => (allOn ? next.delete(e.employeeId) : next.add(e.employeeId)));
      return next;
    });

  const selection = useMemo(() => employees.filter((e) => selected.has(e.employeeId)), [employees, selected]);
  const num = parseFloat(amount);
  // Tenure is undefined without a date, so the partition (and the preview)
  // waits for one — otherwise every employee would read as "skipped".
  const partition = useMemo(
    () =>
      effectiveDate
        ? partitionBulkSelection(selection, effectiveDate, runs)
        : { toCreate: [], skipped: [], alreadyPaid: 0 },
    [selection, effectiveDate, runs],
  );
  const previewLines = bulkPreviewLines(partition, Number.isNaN(num) ? 0 : num, type);
  const blocked = partition.toCreate.length === 0; // zero selected / all skipped / no date

  const submit = () => {
    setError(null);
    if (!category) return setError("Select a category.");
    const amountError = validateAmount(Number.isNaN(num) ? NaN : num);
    if (amountError) return setError(amountError);
    onSave({ partition, form: { type, category, description, amount: num, effectiveDate } });
  };

  const field = { display: "block", width: "100%", marginBottom: "0.75rem", ...inputStyle };
  const label = { fontSize: "0.75rem", fontWeight: 600, color: "var(--ui-text-secondary)", marginBottom: "0.25rem", display: "block" };
  const rowLabel = { display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" };

  return (
    <Modal
      isOpen
      onClose={onClose}
      busy={saving}
      labelledBy={titleId}
      style={{ background: "white", borderRadius: "12px", maxWidth: "520px", width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      {/* The body scrolls, not the dialog, so the shell's × stays pinned. */}
      <div style={{ padding: "1.5rem", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 id={titleId} style={{ margin: 0, fontSize: "1rem", color: "var(--ui-text)" }}>Bulk Add Adjustment</h3>
        </div>

        <label style={label}>Type</label>
        <div style={{ display: "flex", gap: "1rem", marginBottom: "0.75rem" }}>
          {["earning", "deduction"].map((t) => (
            <label key={t} style={{ ...rowLabel, gap: "0.375rem", fontSize: "0.875rem" }}>
              <input
                type="radio"
                checked={type === t}
                onChange={() => {
                  setType(t);
                  setCategory("");
                }}
              />
              {t === "earning" ? "Earning (add to pay)" : "Deduction (subtract from pay)"}
            </label>
          ))}
        </div>

        <label style={label}>Category</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)} style={field}>
          <option value="">Select category…</option>
          {categories.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>

        <label style={label}>Amount (₱) — per employee</label>
        <input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} style={field} />

        <label style={label}>Effective Date</label>
        <input
          type="date"
          value={effectiveDate}
          min={dateClamp.min}
          max={dateClamp.max}
          onChange={(e) => setEffectiveDate(e.target.value)}
          style={field}
        />

        <label style={label}>Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="e.g. Rice allowance — September"
          style={{ ...field, resize: "vertical", fontFamily: "inherit" }}
        />

        <label style={label}>Employees ({effectiveDate ? partition.toCreate.length : selected.size} selected)</label>
        <input type="text" placeholder="Search employees…" value={search} onChange={(e) => setSearch(e.target.value)} style={field} />
        <div style={{ border: "1px solid var(--ui-border)", borderRadius: "6px", maxHeight: "220px", overflowY: "auto", marginBottom: "0.75rem" }}>
          {groups.length === 0 && (
            <div style={{ padding: "0.75rem", color: "var(--ui-text-muted)", fontSize: "0.8125rem" }}>
              {effectiveDate ? "No employees match." : PICKER_NO_DATE}
            </div>
          )}
          {groups.map(([dept, members]) => {
            const allOn = members.every((e) => selected.has(e.employeeId));
            return (
              <div key={dept}>
                <label style={{ ...rowLabel, padding: "0.5rem 0.75rem", background: "var(--ui-surface-recessed)", fontSize: "0.75rem", fontWeight: 600 }}>
                  <input type="checkbox" checked={allOn} onChange={() => toggleGroup(members, allOn)} />
                  {dept} ({members.length})
                </label>
                {members.map((e) => (
                  <label key={e.employeeId} style={{ ...rowLabel, padding: "0.375rem 0.75rem 0.375rem 1.75rem", fontSize: "0.8125rem" }}>
                    <input type="checkbox" checked={selected.has(e.employeeId)} onChange={() => toggle(e.employeeId)} />
                    {e.fullName}
                    <span style={{ color: "var(--ui-text-muted)", fontVariantNumeric: "tabular-nums" }}>EMP-{e.employeeId}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>

        {/* C5/F1 preview block — the only place the operator sees the aggregate money. */}
        {previewLines.length > 0 && (
          <div
            style={{
              background: blocked ? "var(--ui-warning-tint)" : "var(--ui-primary-tint)",
              color: blocked ? "var(--ui-warning-text)" : "var(--ui-primary-hover)",
              borderRadius: "8px",
              padding: "0.625rem 0.875rem",
              fontSize: "0.8125rem",
              marginBottom: "0.75rem",
            }}
          >
            {previewLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        )}
        {selected.size > 0 && !effectiveDate && (
          <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", marginBottom: "0.75rem" }}>
            Pick an effective date to preview.
          </div>
        )}
        {error && (
          <div style={{ background: "var(--ui-danger-tint)", color: "var(--ui-danger-text)", borderRadius: "8px", padding: "0.625rem 0.875rem", fontSize: "0.8125rem", marginBottom: "0.75rem" }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "0.5rem" }}>
          <button onClick={onClose} disabled={saving} style={{ ...inputStyle, cursor: "pointer", background: "white" }}>
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || blocked}
            style={{
              padding: "0.5rem 1rem",
              background: "var(--ui-primary)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "0.8125rem",
              cursor: saving ? "wait" : blocked ? "not-allowed" : "pointer",
              opacity: saving || blocked ? 0.7 : 1,
            }}
          >
            {saving
              ? "Saving…"
              : blocked
                ? "Create entries"
                : `Create ${partition.toCreate.length} ${partition.toCreate.length === 1 ? "entry" : "entries"}`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AdjustmentsPage;
